/**
 * Centralized API Client for CuriousJr Web Revamp
 * Wraps @pw-tech/web-sdk and handles authentication
 * Handles base URL construction internally
 */

import { getLocalStorageValue } from '@/utils/common';
import { CookieKeys, CookieService } from '@/utils/cookie-service';
import { ApiClient as PWApiClient } from '@pw-tech/web-sdk';

// Base URLs for different services
export const BASE_URLS = {
  PP_API:
    process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || process.env.NEXT_PUBLIC_PP_API_BASE_URL || '',
  CJR_API: process.env.NEXT_PUBLIC_CJR_API_BASE_URL || '',
  STORE_API: process.env.NEXT_PUBLIC_STORE_SVC_API || '',
  CDN: process.env.NEXT_PUBLIC_CDN_BASE_URL || '',
} as const;

// Service types for better type safety
export type ServiceType = keyof typeof BASE_URLS;

export const ApiServiceTypeEnum = Object.fromEntries(
  Object.keys(BASE_URLS).map(key => [key, key])
) as Record<keyof typeof BASE_URLS, keyof typeof BASE_URLS>;

/**
 * Constructs a complete URL from a service type and endpoint
 */
const buildUrl = (service: ServiceType, endpoint: string): string => {
  // If endpoint already contains a full URL, return it as is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  // Ensure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  return `${BASE_URLS[service]}${normalizedEndpoint}`;
};

export const getAccessToken = () => {
  return (
    CookieService.getValue(CookieKeys.TOKEN_CONTEXT)?.token ||
    localStorage.getItem('TOKEN') ||
    localStorage.getItem('token')
  );
};

export const setAccessToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('TOKEN', token);
  }
};

export const removeAccessToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('TOKEN');
    localStorage.removeItem('token');
  }
};

interface Config {
  params?: any;
  headers?: any;
}

const getHeaders = (_headers: any) => {
  const fromApp = getLocalStorageValue('from_app') === 'true';
  const clientType = fromApp ? 'MOBILE' : process.env.NEXT_PUBLIC_PP_CLIENT_TYPE;

  return {
    ...{
      version: '510',
      'client-id': process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID || process.env.NEXT_PUBLIC_PP_ORG_ID,
      'client-version': process.env.NEXT_PUBLIC_PP_CLIENT_VERSION,
      'client-type': clientType,
    },
    ..._headers,
  };
};

// Helper function to determine if input is a service type or complete URL
const isServiceType = (input: string): input is ServiceType => {
  return input in BASE_URLS;
};

// Helper function to get the final URL
const getFinalUrl = (serviceOrUrl: string, endpoint?: string): string => {
  if (endpoint) {
    // New way: service + endpoint
    return buildUrl(serviceOrUrl as ServiceType, endpoint);
  } else if (isServiceType(serviceOrUrl)) {
    // Service type without endpoint - not valid
    throw new Error(`Service type '${serviceOrUrl}' provided without endpoint`);
  } else {
    // Old way: complete URL
    return serviceOrUrl;
  }
};

export const Client: any = {
  get: async (serviceOrUrl: string, endpointOrConfig?: string | Config, config: Config = {}) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalConfig: Config;

      if (typeof endpointOrConfig === 'string') {
        // New way: Client.get(service, endpoint, config)
        finalUrl = getFinalUrl(serviceOrUrl, endpointOrConfig);
        finalConfig = config;
      } else {
        // Old way: Client.get(url, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalConfig = (endpointOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.get(finalUrl, { ...finalConfig, headers });
      return res;
    } catch (error: any) {
      throw error;
    }
  },

  post: async (
    serviceOrUrl: string,
    dataOrEndpoint?: any,
    endpointOrConfig?: string | Config,
    config: Config = {}
  ) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalData: any;
      let finalConfig: Config;

      if (typeof dataOrEndpoint === 'string') {
        // New way: Client.post(service, endpoint, data, config)
        finalUrl = getFinalUrl(serviceOrUrl, dataOrEndpoint);
        finalData = endpointOrConfig;
        finalConfig = config;
      } else {
        // Old way: Client.post(url, data, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalData = dataOrEndpoint;
        finalConfig = (endpointOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.post(finalUrl, finalData, { ...finalConfig, headers });
      return res;
    } catch (error: any) {
      throw error;
    }
  },

  delete: async (
    serviceOrUrl: string,
    dataOrEndpoint?: any,
    endpointOrConfig?: string | Config,
    config: Config = {}
  ) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalData: any;
      let finalConfig: Config;

      if (typeof dataOrEndpoint === 'string') {
        // New way: Client.delete(service, endpoint, data, config)
        finalUrl = getFinalUrl(serviceOrUrl, dataOrEndpoint);
        finalData = endpointOrConfig;
        finalConfig = config;
      } else {
        // Old way: Client.delete(url, data, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalData = dataOrEndpoint;
        finalConfig = (endpointOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.delete(finalUrl, finalData, { ...finalConfig, headers });
      return res;
    } catch (error: any) {
      throw error;
    }
  },

  put: async (
    serviceOrUrl: string,
    dataOrEndpoint?: any,
    endpointOrConfig?: string | Config,
    config: Config = {}
  ) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalData: any;
      let finalConfig: Config;

      if (typeof dataOrEndpoint === 'string') {
        // New way: Client.put(service, endpoint, data, config)
        finalUrl = getFinalUrl(serviceOrUrl, dataOrEndpoint);
        finalData = endpointOrConfig;
        finalConfig = config;
      } else {
        // Old way: Client.put(url, data, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalData = dataOrEndpoint;
        finalConfig = (endpointOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.put(finalUrl, finalData, { ...finalConfig, headers });
      return res;
    } catch (error: any) {
      throw error;
    }
  },

  patch: async (
    serviceOrUrl: string,
    dataOrEndpoint?: any,
    endpointOrConfig?: string | Config,
    config: Config = {}
  ) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalData: any;
      let finalConfig: Config;

      if (typeof dataOrEndpoint === 'string') {
        // New way: Client.patch(service, endpoint, data, config)
        finalUrl = getFinalUrl(serviceOrUrl, dataOrEndpoint);
        finalData = endpointOrConfig;
        finalConfig = config;
      } else {
        // Old way: Client.patch(url, data, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalData = dataOrEndpoint;
        finalConfig = (endpointOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.patch(finalUrl, finalData, { ...finalConfig, headers });
      return res;
    } catch (error: any) {
      throw error;
    }
  },

  postMultipart: async (
    serviceOrUrl: string,
    formDataOrEndpoint: FormData | string,
    formDataOrConfig?: FormData | Config,
    config: Config = {}
  ) => {
    try {
      // Handle both old and new calling patterns
      let finalUrl: string;
      let finalFormData: FormData;
      let finalConfig: Config;

      if (typeof formDataOrEndpoint === 'string') {
        // New way: Client.postMultipart(service, endpoint, formData, config)
        finalUrl = getFinalUrl(serviceOrUrl, formDataOrEndpoint);
        finalFormData = formDataOrConfig as FormData;
        finalConfig = config;
      } else {
        // Old way: Client.postMultipart(url, formData, config)
        finalUrl = getFinalUrl(serviceOrUrl);
        finalFormData = formDataOrEndpoint;
        finalConfig = (formDataOrConfig as Config) || {};
      }

      const headers = getHeaders(finalConfig.headers);
      const res = await PWApiClient.postMultipart(finalUrl, finalFormData, {
        ...finalConfig,
        headers,
      });
      return res;
    } catch (error: any) {
      throw error;
    }
  },
};

// Enhanced API Client class for additional functionality
export class ApiClient {
  static async get<T = any>(
    serviceOrUrl: 'CJR_API' | 'PP_API',
    endpointOrConfig?: string | ApiConfig,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.get(serviceOrUrl, endpointOrConfig as any, config);
  }

  static async post<T = any>(
    service: 'CJR_API' | 'PP_API',
    endpoint: string,
    data?: any,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.post(service, endpoint, data, config);
  }

  static async put<T = any>(
    service: 'CJR_API' | 'PP_API',
    data: any,
    endpoint: string,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.put(service, endpoint, data, config);
  }

  static async patch<T = any>(
    service: 'CJR_API' | 'PP_API',
    data: any,
    endpoint: string,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.patch(service, endpoint, data, config);
  }

  static async delete<T = any>(
    serviceOrUrl: 'CJR_API',
    dataOrEndpoint?: any,
    endpointOrConfig?: string | ApiConfig,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.delete(serviceOrUrl, dataOrEndpoint, endpointOrConfig as any, config);
  }

  static async postMultipart<T = any>(
    serviceOrUrl: 'CJR_API',
    formDataOrEndpoint: FormData | string,
    formDataOrConfig?: FormData | ApiConfig,
    config: ApiConfig = {}
  ): Promise<T> {
    return Client.postMultipart(serviceOrUrl, formDataOrEndpoint, formDataOrConfig as any, config);
  }

  static async upload<T = any>(
    serviceOrUrl: 'CJR_API',
    fileOrEndpoint: File | Blob | string,
    fileOrConfig?: File | Blob | ApiConfig,
    config: ApiConfig = {}
  ): Promise<T> {
    // Handle both old and new calling patterns
    let finalServiceOrUrl: string;
    let finalFile: File | Blob;
    let finalConfig: ApiConfig;

    if (typeof fileOrEndpoint === 'string') {
      // New way: ApiClient.upload(service, endpoint, file, config)
      finalServiceOrUrl = serviceOrUrl;
      finalFile = fileOrConfig as File | Blob;
      finalConfig = config;
    } else {
      // Old way: ApiClient.upload(url, file, config)
      finalServiceOrUrl = serviceOrUrl;
      finalFile = fileOrEndpoint;
      finalConfig = (fileOrConfig as ApiConfig) || {};
    }

    const formData = new FormData();
    formData.append('file', finalFile);

    // Add any additional form fields
    if (finalConfig.params) {
      Object.entries(finalConfig.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
    }

    return this.postMultipart<T>(finalServiceOrUrl as 'CJR_API', formData, finalConfig);
  }

  // Utility methods
  static createAbortController(): AbortController {
    return new AbortController();
  }
}

// Types for external use
export interface ApiConfig {
  params?: any;
  headers?: any;
  signal?: AbortSignal;
  retries?: number;
  timeout?: number;
}

export type ApiResponse<T = any> = T;

export interface TokenContext {
  token: string;
  refreshToken?: string;
  expiresAt?: number;
  userId?: string;
}

// Additional utility functions for token management
export const isTokenExpired = (): boolean => {
  if (typeof window === 'undefined') return true;

  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; TOKEN_CONTEXT=`);
    if (parts.length === 2) {
      const tokenContextStr = parts.pop()?.split(';').shift();
      if (tokenContextStr) {
        const tokenContext: TokenContext = JSON.parse(tokenContextStr);
        if (tokenContext.expiresAt) {
          return Date.now() > tokenContext.expiresAt;
        }
      }
    }
  } catch (error) {
    console.warn('Failed to check token expiration:', error);
  }
  return false;
};

export default ApiClient;
